import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { GAME_NAV_POTION_IDS, GAME_POTIONS } from "../constants/potions";
import {
  assignPotionShortcutKey,
  formatShortcutKeyFromEvent,
  POTION_SHORTCUTS_MODE_OPTIONS,
  POTION_SHORTCUTS_MODES,
  readPotionShortcutBindings,
  readPotionShortcutsMode,
  subscribePotionShortcutsPreference,
  writePotionShortcutsMode,
} from "../utils/gamePotionShortcutsPreference";
import KeyboardKeyCap from "./KeyboardKeyCap";
import "./GameDifficultySelect.css";
import "./GameKeyboardShortcutsScreen.css";

const navPotions = GAME_NAV_POTION_IDS.map((id) =>
  GAME_POTIONS.find((potion) => potion.id === id)
).filter(Boolean);

export default function GameKeyboardShortcutsScreen() {
  const [mode, setMode] = useState(() => readPotionShortcutsMode());
  const [bindings, setBindings] = useState(() => readPotionShortcutBindings());
  const [listeningPotionId, setListeningPotionId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = useMemo(
    () =>
      POTION_SHORTCUTS_MODE_OPTIONS.find((option) => option.value === mode) ||
      POTION_SHORTCUTS_MODE_OPTIONS[1],
    [mode]
  );

  useEffect(() => subscribePotionShortcutsPreference(({ mode: nextMode, bindings: nextBindings }) => {
    setMode(nextMode);
    setBindings(nextBindings);
  }), []);

  useEffect(() => {
    if (!listeningPotionId) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setListeningPotionId(null);
        return;
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        const nextBindings = assignPotionShortcutKey(listeningPotionId, null);
        setBindings(nextBindings);
        setListeningPotionId(null);
        return;
      }

      const label = formatShortcutKeyFromEvent(event);
      if (!label) return;

      event.preventDefault();
      const nextBindings = assignPotionShortcutKey(listeningPotionId, label);
      setBindings(nextBindings);
      setListeningPotionId(null);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [listeningPotionId]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownOpen]);

  const handleModeSelect = (value) => {
    writePotionShortcutsMode(value);
    setMode(value);
    setDropdownOpen(false);
  };

  const startListening = useCallback((potionId) => {
    setDropdownOpen(false);
    setListeningPotionId(potionId);
  }, []);

  return (
    <div className="game-shortcuts-screen">
      <header className="game-shortcuts-screen__header">
        <h2 className="game-difficulty-title">Raccourcis clavier</h2>
      </header>

      <div className="game-shortcuts-screen__body">
        <div
          className={`game-shortcuts-dropdown${
            dropdownOpen ? " game-shortcuts-dropdown--open" : ""
          }`}
          ref={dropdownRef}
        >
          <button
            type="button"
            className="game-shortcuts-dropdown__trigger"
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            onClick={() => setDropdownOpen((open) => !open)}
          >
            <span className="game-shortcuts-dropdown__label">{selectedOption.label}</span>
            <ChevronDown
              size={16}
              strokeWidth={2}
              className="game-shortcuts-dropdown__chevron"
              aria-hidden="true"
            />
          </button>

          {dropdownOpen && (
            <ul className="game-shortcuts-dropdown__menu" role="listbox">
              {POTION_SHORTCUTS_MODE_OPTIONS.map((option) => (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === mode}
                    className={`game-shortcuts-dropdown__option${
                      option.value === mode ? " game-shortcuts-dropdown__option--selected" : ""
                    }`}
                    onClick={() => handleModeSelect(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {listeningPotionId ? (
          <p className="game-shortcuts-screen__hint" aria-live="polite">
            Appuyez sur une touche de 0 à 9 ou F1 à F10… (Échap pour annuler, Suppr pour effacer)
          </p>
        ) : (
          <p className="game-shortcuts-screen__hint game-shortcuts-screen__hint--idle">
            Cliquez sur une touche, puis assignez un chiffre de 0 à 9 (rangée du haut ou pavé
            numérique) ou une touche F1 à F10. Pendant le jeu, le raccourci fonctionne même dans le
            champ de réponse.
          </p>
        )}

        <div className="game-shortcuts-grid" role="list">
          {navPotions.map((potion) => {
            const assignedKey = bindings[potion.id] || "";
            const isListening = listeningPotionId === potion.id;

            return (
              <div key={potion.id} className="game-shortcuts-cell" role="listitem">
                <img
                  src={potion.image}
                  alt=""
                  className={`game-shortcuts-cell__image game-shortcuts-cell__image--${potion.id}`}
                  draggable={false}
                  aria-hidden="true"
                />
                <p className="game-shortcuts-cell__name">{potion.name}</p>
                <KeyboardKeyCap
                  as="button"
                  type="button"
                  className={`game-shortcuts-cell__key${
                    isListening ? " game-shortcuts-cell__key--listening" : ""
                  }${assignedKey ? " game-shortcuts-cell__key--assigned" : ""}`}
                  onClick={() => startListening(potion.id)}
                  aria-label={
                    assignedKey
                      ? `Raccourci ${assignedKey} pour ${potion.name}. Cliquer pour modifier.`
                      : `Assigner un raccourci pour ${potion.name}`
                  }
                >
                  {isListening ? "…" : assignedKey || "—"}
                </KeyboardKeyCap>
              </div>
            );
          })}
        </div>

        {mode === POTION_SHORTCUTS_MODES.DISABLED ? (
          <p className="game-shortcuts-screen__note">
            Les raccourcis sont enregistrés mais désactivés pendant le jeu.
          </p>
        ) : null}
      </div>
    </div>
  );
}
